import { useEffect, useRef, useState } from 'react';
import { createAppSyncClient } from '../appsync-client.ts';
import { transactionDataFields } from '../lib/graphql-operations.ts';
export interface TransactionAnalyticsRecord {
  analyticsClientId: string | null;
  cartId: string | null;
  firstName: string | null;
  isBlacklisted: boolean;
  items: unknown[];
  paymentType: string | null;
  referrer: string | null;
  sourceCode: string | null;
  sourceCodePremium: string | null;
  state: string | null;
  supporterGlobalId: string | null;
  time: string | null;
  transactionId: string | null;
}
export interface UseTransactionSubscriptionOptions {
  /** AppSync HTTP endpoint URL (https://...) */
  appSyncUrl: string;
  /** AppSync API key for authentication */
  apiKey: string;
  /** Optional source code filter — server-side filtering via AppSync subscription */
  sourceCode?: string;
  /** Whether the subscription is enabled (default: true) */
  enabled?: boolean;
  /** Called once per incoming transaction record */
  onTransaction: (record: TransactionAnalyticsRecord) => void;
}
export interface UseTransactionSubscriptionResult {
  /** Whether the AppSync subscription connection is active */
  isConnected: boolean;
  /** Whether an error has occurred */
  isError: boolean;
  /** The error, if any */
  error: Error | null;
}
const subscriptionFields = `{${transactionDataFields}
  }`;

// Only declares variables for filters that are set, so AppSync doesn't
// treat missing variables as null-match filters.
const buildSubscriptionQuery = (filters: {
  sourceCode?: string;
}): {
  query: string;
  variables: Record<string, unknown>;
} => {
  const varDefs: string[] = [];
  const args: string[] = [];
  const variables: Record<string, unknown> = {};
  if (filters.sourceCode) {
    varDefs.push('$sourceCode: String');
    args.push('sourceCode: $sourceCode');
    variables.sourceCode = filters.sourceCode;
  }
  const varDefsStr = varDefs.length > 0 ? `(${varDefs.join(', ')})` : '';
  const argsStr = args.length > 0 ? `(${args.join(', ')})` : '';
  const query = `subscription OnTransaction${varDefsStr} {
  onTransaction${argsStr} ${subscriptionFields}
}`;
  return {
    query,
    variables
  };
};

/**
 * Lightweight push-based subscription hook. Each incoming transaction is
 * delivered exactly once via the `onTransaction` callback — no caching,
 * no accumulation, no React Query dependency.
 */
export const useTransactionSubscription = (options: UseTransactionSubscriptionOptions): UseTransactionSubscriptionResult => {
  const {
    appSyncUrl,
    apiKey,
    sourceCode,
    enabled = true,
    onTransaction
  } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [clientError, setClientError] = useState<Error | null>(null);
  const onTransactionRef = useRef(onTransaction);
  onTransactionRef.current = onTransaction;
  useEffect(() => {
    if (!enabled || !appSyncUrl || !apiKey) {
      return;
    }
    const client = createAppSyncClient({
      appSyncUrl,
      apiKey,
      maxRetries: 5,
      onConnected: () => {
        setIsConnected(true);
        setClientError(null);
      },
      onDisconnected: () => {
        setIsConnected(false);
      },
      onError: error => {
        setClientError(error);
        setIsConnected(false);
      }
    });
    const {
      query,
      variables
    } = buildSubscriptionQuery({
      sourceCode
    });
    const subscription = client.subscribe(query, variables, rawData => {
      const payload = rawData as Record<string, unknown> | undefined;
      if (!payload) {
        return;
      }
      const record = payload.onTransaction as TransactionAnalyticsRecord | undefined;
      if (record) {
        onTransactionRef.current(record);
      }
    });
    client.connect();
    return () => {
      subscription.unsubscribe();
      client.disconnect();
      setIsConnected(false);
    };
  }, [appSyncUrl, apiKey, sourceCode, enabled]);
  return {
    isConnected,
    isError: !!clientError,
    error: clientError
  };
};